import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const PERIODS = [
  { value: "30",  label: "Últimos 30 dias" },
  { value: "90",  label: "Últimos 90 dias" },
  { value: "180", label: "Últimos 180 dias" },
  { value: "365", label: "Último ano" },
  { value: "all", label: "Todo o período" },
];

function fmtDate(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}
const today = () => new Date().toISOString().slice(0, 10);

const LEVEL_STYLE = {
  critical:  { card: "bg-rose-50 border-rose-300", badge: "bg-rose-200 text-rose-900 border-rose-400", label: "CRÍTICO", icon: "fa-triangle-exclamation" },
  warning:   { card: "bg-amber-50 border-amber-300", badge: "bg-amber-200 text-amber-900 border-amber-400", label: "ATENÇÃO ALTA", icon: "fa-circle-exclamation" },
  attention: { card: "bg-yellow-50 border-yellow-300", badge: "bg-yellow-200 text-yellow-900 border-yellow-400", label: "ATENÇÃO", icon: "fa-circle-info" },
  ok:        { card: "bg-emerald-50 border-emerald-300", badge: "bg-emerald-200 text-emerald-900 border-emerald-400", label: "ESTÁVEL", icon: "fa-circle-check" },
};

export default function RiskMapConsolidated({ companyIdOverride }) {
  const params = useParams();
  const companyId = companyIdOverride || params.companyId;
  const navigate = useNavigate();
  const [period, setPeriod] = useState("90");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = {};
      if (period !== "all") {
        params.start = fmtDate(parseInt(period, 10));
        params.end = today();
      }
      const r = await api.get(`/risk-map/${companyId}`, { params });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [companyId, period]);  // eslint-disable-line

  if (!data && !loading) return null;

  return (
    <div className="space-y-6" data-testid="risk-map-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">Mapa de Risco Consolidado</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">
            {data?.company?.razao_social || "Carregando…"}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Visão única por GHE cruzando <b>NR-01 psicossocial</b>, <b>Absenteísmo</b>,
            <b> CIDs F/M</b>, <b>Reincidência</b> e <b>Ações pendentes</b>.
          </p>
        </div>
        <div className="flex gap-2">
          <label>
            <div className="overline mb-1">Período</div>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-slate-300 px-3 py-2" data-testid="risk-map-period">
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          <button onClick={() => window.print()} className="btn btn-ghost border border-slate-300 mt-6" data-testid="risk-map-print">
            <i className="fa-solid fa-print"></i> Imprimir
          </button>
        </div>
      </header>

      {loading && <div className="card text-center text-slate-500">Carregando dados consolidados…</div>}

      {data && !loading && (
        <>
          <SummaryCards totals={data.totals} />
          {data.rows.length === 0 ? (
            <div className="card text-center py-12">
              <i className="fa-solid fa-circle-question text-5xl text-slate-300 mb-3"></i>
              <h2 className="font-display text-xl font-extrabold">Sem dados para consolidar</h2>
              <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
                O cruzamento requer pelo menos uma <b>avaliação NR-01 com respostas</b> OU
                colaboradores com GHE preenchido E atestados registrados no período.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Link to="/app/avaliacoes" className="btn btn-ghost border border-emerald-300 text-emerald-900">
                  <i className="fa-solid fa-clipboard-list"></i> Ir para Avaliações
                </Link>
                <Link to={`/app/absenteismo/colaboradores?empresa=${companyId}`} className="btn btn-ghost border border-emerald-300 text-emerald-900">
                  <i className="fa-solid fa-users"></i> Cadastrar Colaboradores
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {data.rows.map((r) => <GheRow key={r.ghe} r={r} companyId={companyId} navigate={navigate} />)}
            </div>
          )}

          <div className="card bg-slate-50 border-slate-200">
            <div className="overline mb-2">Como o nível geral é calculado</div>
            <p className="text-xs text-slate-600 leading-relaxed">
              Combina 7 sinais: <b>NR-01 score</b> (≥2.5 e ≥3.5 disparam pontos),
              <b> dias afastados</b> (≥10 e ≥30), <b>CIDs F</b> (≥3), <b>CIDs M</b> (≥3),
              <b> ações pendentes</b> (≥5) e <b>reincidência</b> (≥2 colaboradores com 3+ atestados).
              <span className="block mt-1">
                <span className="inline-block w-3 h-3 bg-rose-200 align-middle mr-1"></span>5+ pontos = CRÍTICO ·
                <span className="inline-block w-3 h-3 bg-amber-200 align-middle ml-2 mr-1"></span>3-4 = ATENÇÃO ALTA ·
                <span className="inline-block w-3 h-3 bg-yellow-200 align-middle ml-2 mr-1"></span>1-2 = ATENÇÃO ·
                <span className="inline-block w-3 h-3 bg-emerald-200 align-middle ml-2 mr-1"></span>0 = ESTÁVEL
              </span>
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCards({ totals }) {
  const items = [
    { label: "GHEs analisados", value: totals.ghe_count, icon: "fa-layer-group", tone: "slate" },
    { label: "GHEs críticos", value: totals.critical_ghes, icon: "fa-triangle-exclamation", tone: totals.critical_ghes > 0 ? "rose" : "slate" },
    { label: "Atenção alta", value: totals.warning_ghes, icon: "fa-circle-exclamation", tone: totals.warning_ghes > 0 ? "amber" : "slate" },
    { label: "Em atenção", value: totals.attention_ghes ?? 0, icon: "fa-circle-info", tone: (totals.attention_ghes ?? 0) > 0 ? "amber" : "slate" },
    { label: "Dias perdidos total", value: totals.total_abs_days, icon: "fa-calendar-xmark", tone: "rose" },
    { label: "Ações pendentes", value: totals.total_pending_actions_nr01 + totals.total_pending_actions_abs, icon: "fa-list-check", tone: "amber" },
  ];
  const tones = {
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-200",
    rose: "bg-rose-50 text-rose-900 border-rose-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    slate: "bg-slate-50 text-slate-900 border-slate-200",
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="risk-map-summary">
      {items.map((it) => (
        <div key={it.label} className={`card border-2 ${tones[it.tone]}`}>
          <div className="flex items-start justify-between">
            <div className="overline">{it.label}</div>
            <i className={`fa-solid ${it.icon}`}></i>
          </div>
          <div className="font-display text-2xl font-extrabold mt-2">{it.value}</div>
        </div>
      ))}
    </div>
  );
}

function GheRow({ r, companyId, navigate }) {
  const style = LEVEL_STYLE[r.overall_level];
  return (
    <div className={`card border-l-8 ${style.card}`} data-testid={`risk-row-${r.ghe}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display text-2xl font-extrabold text-slate-900">{r.ghe}</h3>
          {r.nr01_responses > 0 && (
            <div className="text-xs text-slate-600 mt-1">
              {r.nr01_responses} respostas NR-01 · {r.nr01_workers} trabalhador(es) esperados ·
              {r.nr01_assessments} avaliação(ões)
            </div>
          )}
        </div>
        <div className={`text-xs font-bold uppercase tracking-wider border px-3 py-1.5 ${style.badge}`}>
          <i className={`fa-solid ${style.icon} mr-1`}></i>{style.label}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-7 gap-3 text-center">
        <Metric label="NR-01" value={r.nr01_score || "—"} sub={r.nr01_label} testid={`metric-nr01-${r.ghe}`} />
        <Metric label="Dias afast." value={r.abs_days} sub={`${r.abs_count} atestados`} testid={`metric-days-${r.ghe}`} />
        <Metric label="CIDs F" value={r.cid_f} sub={r.cid_f >= 3 ? "Psicossocial!" : "saúde mental"} highlight={r.cid_f >= 3} />
        <Metric label="CIDs M" value={r.cid_m} sub={r.cid_m >= 3 ? "Ergonomia!" : "musculoesquelético"} highlight={r.cid_m >= 3} />
        <Metric label="Top CID" value={r.top_cid || "—"} sub={r.top_cid ? `${r.top_cid_count} caso(s)` : ""} mono />
        <Metric label="Reincidência" value={r.reincident_employees} sub={r.reincident_employees >= 2 ? "Atenção!" : "colaboradores"} highlight={r.reincident_employees >= 2} />
        <Metric label="Ações pendentes" value={r.pending_actions} sub="NR-01" highlight={r.pending_actions >= 5} />
      </div>

      <div className="mt-4 pt-3 border-t border-slate-200 flex flex-wrap justify-end gap-2">
        <button onClick={() => navigate(`/app/empresas/${companyId}/mapa`)} className="text-xs text-emerald-800 hover:underline">
          <i className="fa-solid fa-fire mr-1"></i>Ver heatmap NR-01
        </button>
        <span className="text-slate-300">·</span>
        <button onClick={() => navigate(`/app/absenteismo/inteligencia?empresa=${companyId}`)} className="text-xs text-emerald-800 hover:underline">
          <i className="fa-solid fa-brain mr-1"></i>Análise IA Absenteísmo
        </button>
        <span className="text-slate-300">·</span>
        <button onClick={() => navigate(`/app/absenteismo/relatorio?empresa=${companyId}`)} className="text-xs text-emerald-800 hover:underline">
          <i className="fa-solid fa-file-pdf mr-1"></i>Relatório PDF
        </button>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, highlight, mono, testid }) {
  return (
    <div className={`border ${highlight ? "border-rose-300 bg-white" : "border-slate-200 bg-white"} p-3`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className={`font-display font-extrabold mt-1 ${mono ? "font-mono text-base" : "text-xl"} ${highlight ? "text-rose-700" : "text-slate-900"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
